import type { CarrierNew } from '@eti/schema-types';
import { EventSegmentType } from '@eti/schema-types';
import { date } from '@eti/utils';
import type { OriginalVersionType, SegmentsType } from '../../../../models';
import { getTotalTravelDuration } from '../common/utils/utils';

const getNumberOfStops = (segments: SegmentsType): number =>
  segments.filter((segment) => segment?.__typename === 'EventSegment').length;

type EventSegmentTypeWithoutStop = Exclude<EventSegmentType, typeof EventSegmentType.Stop>;
const getUniqueEventTypes = (segments: SegmentsType): EventSegmentTypeWithoutStop[] =>
  Array.from(
    new Set(
      segments.flatMap((segment) => {
        if (segment && segment.__typename === 'EventSegment') {
          const { types } = segment;
          if (types) {
            return types.filter(
              (t): t is Exclude<EventSegmentTypeWithoutStop, null> =>
                t !== null && t !== EventSegmentType.Stop,
            );
          }
          return [];
        }
        return [];
      }),
    ),
  );

export const getNumberOfTechnicalStops = (segments: SegmentsType): number =>
  segments.reduce((numberOfTechnicalStops, segment) => {
    if (segment?.__typename === 'TripSegment') {
      return numberOfTechnicalStops + segment.numberOfTechnicalStops;
    }
    return numberOfTechnicalStops;
  }, 0);

export const getLatestBoundInfo = (segments: SegmentsType) => {
  const firstSegment = segments[0];
  const lastSegment = segments[segments.length - 1];

  let origin = '';
  let departuredAt = '';
  let departureCode = '';

  if (firstSegment?.__typename === 'TripSegment') {
    origin = firstSegment.origin.cityName;
    departureCode = firstSegment.origin.code;
    departuredAt = firstSegment.departuredAt;
  }

  let destination = '';
  let arrivedAt = '';
  let arrivalCode = '';
  if (lastSegment?.__typename === 'TripSegment') {
    destination = lastSegment.destination.cityName;
    arrivalCode = lastSegment.destination.code;
    arrivedAt = lastSegment.arrivedAt;
  }

  const uniqueEventTypes = getUniqueEventTypes(segments);

  return {
    arrivedAt,
    arrivalCode,
    departuredAt,
    departureCode,
    destination,
    duration: getTotalTravelDuration(segments),
    numberOfStops: getNumberOfStops(segments),
    origin,
    uniqueEventTypes,
  };
};

export const getOriginalBoundInfo = (originalVersion: NonNullable<OriginalVersionType>) => {
  const firstSegment = originalVersion.originalSegments[0];
  const lastSegment = originalVersion.originalSegments[originalVersion.originalSegments.length - 1];

  return {
    arrivedAt: lastSegment.arrivedAt,
    departuredAt: firstSegment.departuredAt,
    durationInDays: date.durationInDays(
      new Date(firstSegment.departuredAt),
      new Date(lastSegment.arrivedAt),
    ),
    numberOfStops: originalVersion.originalSegments.length - 1,
  };
};

export const getUniqueMarketingCarriersFromSegments = (segments: SegmentsType): CarrierNew[] => {
  return segments.reduce((acc: CarrierNew[], segment) => {
    if (
      segment &&
      segment.__typename === 'TripSegment' &&
      segment.marketingCarrier?.code &&
      !acc.find(({ code }) => code === segment.marketingCarrier?.code)
    ) {
      acc.push(segment.marketingCarrier);
    }
    return acc;
  }, []);
};
